import { Skeleton } from '../../../modules/design-system';
import type { ApiClient } from '../../../modules/api';
import { useFooterLinks } from '../../../modules/footerLinks';

export interface MyPacksFooterProps {
  client: ApiClient;
  /** `data-co-component` for the Design Constructor inspector. Defaults
   *  to `MyPacksFooter`; the Delivery shell passes `DeliveryFooter` so
   *  partners can theme it independently from the MyPacks footer. */
  inspectName?: 'MyPacksFooter' | 'DeliveryFooter';
}

/** Skeleton pills shown while the links are loading — mirrors the previous
 *  three-link layout so the footer height doesn't jump once they land. */
const LOADING_PLACEHOLDER_COUNT = 3;

// Per-inspectName CSS var blocks live as STATIC literals so the
// theme-constructor's schema-invariant test (a regex grep over source
// text) can prove each registry knob is read. Each namespace is fully
// INDEPENDENT — Delivery does NOT cascade through MyPacks, so a
// MyPacks override never leaks onto the Delivery footer (operators
// build the two pages in isolation).
const STYLE_DELIVERY = {
  fontFamily: 'var(--co-DeliveryFooter-fontFamily, Inter), system-ui, sans-serif',
  linkColor: 'var(--co-DeliveryFooter-linkColor, #DCDCDC)',
  linkSize: 'var(--co-DeliveryFooter-linkSize, 13px)',
} as const;

const STYLE_DEFAULT = {
  fontFamily: 'var(--co-MyPacksFooter-fontFamily, Inter), system-ui, sans-serif',
  linkColor: 'var(--co-MyPacksFooter-linkColor, #DCDCDC)',
  linkSize: 'var(--co-MyPacksFooter-linkSize, 13px)',
} as const;

/**
 * Footer with the operator's partner links (configured in the operator
 * console / admin, served by GET /iframe/v1/footer_links). Replaces the old
 * static FAQ / Terms & Conditions / Privacy Policy placeholders. While the
 * list is loading it shows skeleton pills; an operator with no links
 * configured gets no footer at all.
 */
export function MyPacksFooter({
  client,
  inspectName = 'MyPacksFooter',
}: MyPacksFooterProps) {
  const links = useFooterLinks(client);
  const tokens = inspectName === 'DeliveryFooter' ? STYLE_DELIVERY : STYLE_DEFAULT;

  if (links !== null && links.length === 0) return null;

  return (
    <footer
      data-co-component={inspectName}
      className="
        flex flex-wrap items-center justify-center
        gap-x-4 gap-y-1 px-4 py-6
        @[600px]:gap-x-[24px]
      "
      style={{
        fontFamily: tokens.fontFamily,
      }}
    >
      {links === null
        ? Array.from({ length: LOADING_PLACEHOLDER_COUNT }, (_, i) => (
            <Skeleton key={i} className="h-[14px] w-24" />
          ))
        : links.map((link) => (
            <a
              key={`${link.label}-${link.url}`}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap hover:underline"
              style={{
                color: tokens.linkColor,
                fontSize: tokens.linkSize,
                lineHeight: '20px',
                letterSpacing: '-0.1px',
              }}
            >
              {link.label}
            </a>
          ))}
    </footer>
  );
}
